/**
 * Spot Filtering Module
 * Consolidates all spot filtering logic for the temporal parking algorithm
 *
 * Filters are applied in order:
 * 1. Size compatibility (aircraft fits in spot)
 * 2. Time availability (no scheduling conflict)
 * 3. Geometry collision (gap spots only - Phase 3)
 */

import { intersects } from "./bitmap";
import { Point, polygonsIntersect } from "./geometryUtils";
import { isSpotInStagingZone } from "./stagingArea";
import { FilterBreakdown, ParkingRequest, Placement, Spot, Zone } from "./types";

/**
 * Check if spot is enabled (not disabled directly or by zone)
 *
 * Spots can be disabled individually or by disabling their entire zone.
 * This is useful for maintenance, construction, or temporarily closing areas.
 *
 * @param spot - The spot being evaluated
 * @param zones - Array of zones (to check if spot's zone is disabled)
 * @returns true if spot is enabled and zone is enabled, false otherwise
 *
 * @example
 * ```typescript
 * const spot = { disabled: false, zoneId: "A" };
 * const zones = [{ id: "A", disabled: false }];
 * isSpotEnabled(spot, zones) // true
 *
 * const disabledSpot = { disabled: true, zoneId: "A" };
 * isSpotEnabled(disabledSpot, zones) // false (spot disabled)
 *
 * const spotInDisabledZone = { disabled: false, zoneId: "B" };
 * const zonesWithDisabled = [{ id: "B", disabled: true }];
 * isSpotEnabled(spotInDisabledZone, zonesWithDisabled) // false (zone disabled)
 * ```
 */
export function isSpotEnabled(spot: Spot, zones: Zone[]): boolean {
  // Check if spot is directly disabled
  if (spot.disabled === true) return false;

  // Check if spot's zone is disabled
  const zone = zones.find((z) => z.id === spot.zoneId);
  if (zone && zone.disabled === true) return false;

  // Spot is enabled (or zone not found - fail safe to enabled)
  return true;
}

/**
 * Check if aircraft size is compatible with spot
 *
 * Uses array-based policy where spots explicitly list which aircraft sizes they allow.
 *
 * @param spot - The spot being evaluated
 * @param aircraft - The aircraft being placed
 * @returns true if aircraft size is in spot's allowedSizes
 *
 * @example
 * ```typescript
 * const spot = { allowedSizes: ["small", "midsize"] };
 * const smallAircraft = { sizeCategory: "small" };
 * isSizeCompatible(spot, smallAircraft) // true
 * ```
 */
export function isSizeCompatible(
  spot: Spot,
  aircraft: ParkingRequest
): boolean {
  return spot.allowedSizes.includes(aircraft.sizeCategory);
}

/**
 * Check if spot is time-available (no bitmap conflict)
 *
 * Uses bitwise operations to check if the aircraft's dwell time
 * overlaps with the spot's existing occupancy bitmap.
 *
 * @param spot - The spot being evaluated
 * @param dwellMask - Time mask for aircraft dwell period
 * @returns true if no time overlap with spot's occupancy bitmap
 *
 * @example
 * ```typescript
 * const spot = { occupancy: makeEmptyMask() };
 * const dwellMask = makeEmptyMask();
 * setRange(dwellMask, 10, 20);
 * isTimeAvailable(spot, dwellMask) // true (no conflict)
 * ```
 */
export function isTimeAvailable(
  spot: Spot,
  dwellMask: BigUint64Array
): boolean {
  return !intersects(spot.occupancy, dwellMask);
}

/**
 * Check if aircraft dwell duration is within spot's limit
 *
 * Overflow/emergency spots can have max_dwell_minutes limits to prevent
 * long-term parking. This is a hard constraint - aircraft exceeding the
 * limit cannot use the spot.
 *
 * @param spot - The spot being evaluated
 * @param aircraft - The aircraft being placed
 * @returns true if no limit or dwell within limit, false if exceeds limit
 *
 * @example
 * ```typescript
 * const spot = { max_dwell_minutes: 60 }; // 1 hour overflow spot
 * const aircraft = { arrivalBucket: 0, departureBucket: 4 }; // 1 hour stay
 * isDurationValid(spot, aircraft) // true (within limit)
 *
 * const longStay = { arrivalBucket: 0, departureBucket: 12 }; // 3 hour stay
 * isDurationValid(spot, longStay) // false (exceeds 1 hour limit)
 * ```
 */
export function isDurationValid(
  spot: Spot,
  aircraft: ParkingRequest
): boolean {
  // No limit set - spot accepts any duration
  if (!spot.max_dwell_minutes) return true;

  // Calculate aircraft dwell duration in minutes
  const dwellBuckets = aircraft.departureBucket - aircraft.arrivalBucket;
  const dwellMinutes = dwellBuckets * 15; // 15 minutes per bucket

  // Check if within limit
  return dwellMinutes <= spot.max_dwell_minutes;
}

/**
 * Check if spot has geometry collision with already-placed aircraft
 * Only applies to gap spots - returns false for regular spots
 *
 * Gap spots must be completely free and clear:
 * - Required spots must be occupied (Phase 2d - implemented)
 * - No collision with any parked aircraft (Phase 3 - implemented)
 *
 * @param spot - The spot being evaluated (with required shape field)
 * @param aircraft - The aircraft being placed (unused but kept for signature consistency)
 * @param currentPlacement - Current placement state
 * @param placedAircraftPolygons - Map of aircraft ID → footprint polygon (from main loop)
 * @returns true if collision detected (gap spot cannot be used)
 */
export function hasGeometryCollision(
  spot: Spot,
  aircraft: ParkingRequest,
  currentPlacement: Placement,
  placedAircraftPolygons?: Map<string, Point[]>
  // shapes parameter REMOVED - use spot.shape instead
): boolean {
  // Only check gap spots
  if (!spot.isGapSpot) return false;

  // Phase 2d: Check if all required spots are occupied
  if (spot.requiresOccupiedSpots && spot.requiresOccupiedSpots.length > 0) {
    const allOccupied = spot.requiresOccupiedSpots.every((requiredSpotId) => {
      // Check if any aircraft is placed in this required spot
      return Object.values(currentPlacement).some((placementData) => {
        if ("spotId" in placementData) {
          return placementData.spotId === requiredSpotId;
        }
        return false; // Skip segmented placements
      });
    });

    if (!allOccupied) {
      return true; // Not all dependencies met - treat as collision/unavailable
    }
  }

  // Phase 3: Geometry collision check
  // TODO: This uses size-based approximations. Replace with real aircraft geometry when available.
  if (!placedAircraftPolygons) {
    return false; // No geometry data available - skip check
  }

  // No null check needed - shape is required
  // Check each placed aircraft polygon
  for (const [aircraftId, aircraftPolygon] of placedAircraftPolygons) {
    // Check if this aircraft is in one of the required spots
    const placementData = currentPlacement[aircraftId];
    if (!placementData || !("spotId" in placementData)) continue;

    const isInRequiredSpot = spot.requiresOccupiedSpots?.includes(
      placementData.spotId
    );
    if (!isInRequiredSpot) continue;

    // Check collision between aircraft polygon and gap spot polygon
    if (polygonsIntersect(aircraftPolygon, spot.shape.points)) {
      return true; // COLLISION! Parked aircraft overlaps gap spot
    }
  }

  // No collision detected - gap spot is clear
  return false;
}

/**
 * Filter spots to only those available for this aircraft
 * Combines all filtering logic in one place
 *
 * Filters applied in order:
 * 0. Enabled status (spot and zone not disabled)
 * 1. Size compatibility (aircraft fits in spot)
 * 2. Time availability (no scheduling conflict)
 * 3. Duration limit (within max dwell time)
 * 4. Geometry collision (gap spots only - no physical collision)
 *
 * @param spots - All spots to filter
 * @param aircraft - Aircraft being placed
 * @param dwellMask - Time mask for aircraft dwell
 * @param currentPlacement - Current placement state
 * @param placedAircraftPolygons - Optional polygon map for geometry checking (Phase 3)
 * @param shapes - Optional shapes for geometry checking (Phase 3)
 * @param zones - Array of zones (to check if spot's zone is disabled)
 * @returns Filtered list of available spots
 *
 * @example
 * ```typescript
 * const availableSpots = getAvailableSpots(
 *   spots,
 *   aircraft,
 *   dwellMask,
 *   placement,
 *   placedAircraftPolygons,
 *   shapes,
 *   zones
 * );
 * // Returns only spots that pass all filters
 * ```
 */
export function getAvailableSpots(
  spots: Spot[],
  aircraft: ParkingRequest,
  dwellMask: BigUint64Array,
  currentPlacement: Placement,
  placedAircraftPolygons?: Map<string, Point[]>,
  shapes?: Array<{ id: string; points?: Point[] }>,
  zones: Zone[] = []
): Spot[] {
  return spots.filter((spot) => {
    // Filter 0: Enabled status (spot and zone not disabled)
    if (!isSpotEnabled(spot, zones)) return false;

    // Filter 1: Size compatibility
    if (!isSizeCompatible(spot, aircraft)) return false;

    // Filter 2: Time availability
    if (!isTimeAvailable(spot, dwellMask)) return false;

    // Filter 3: Duration limit
    if (!isDurationValid(spot, aircraft)) return false;

    // Filter 4: Geometry collision (gap spots only - Phase 3)
    if (
      hasGeometryCollision(
        spot,
        aircraft,
        currentPlacement,
        placedAircraftPolygons
        // shapes parameter removed - using spot.shape
      )
    ) {
      return false;
    }

    return true;
  });
}

/**
 * Filter spots with detailed breakdown tracking
 * Returns both available spots AND a breakdown showing how many spots were eliminated by each filter
 *
 * Filters applied in order:
 * 0. Enabled status (spot and zone not disabled)
 * 1. Size compatibility (aircraft fits in spot)
 * 2. Time availability (no scheduling conflict)
 * 3. Duration limit (within max dwell time)
 * 4. Geometry collision (gap spots only - no physical collision)
 *
 * @param spots - All spots to filter
 * @param aircraft - Aircraft being placed
 * @param dwellMask - Time mask for aircraft dwell
 * @param currentPlacement - Current placement state
 * @param placedAircraftPolygons - Optional polygon map for geometry checking (Phase 3)
 * @param shapes - Optional shapes for geometry checking (Phase 3)
 * @param zones - Array of zones (to check if spot's zone is disabled)
 * @returns Object with available spots and detailed filter breakdown
 *
 * @example
 * ```typescript
 * const { availableSpots, breakdown } = getAvailableSpotsWithBreakdown(
 *   spots,
 *   aircraft,
 *   dwellMask,
 *   placement,
 *   placedAircraftPolygons,
 *   shapes,
 *   zones
 * );
 * // breakdown: { total: 62, disabled: 5, sizeIncompatible: 45, timeConflict: 10, durationExceeded: 2, geometryCollision: 0, available: 0 }
 * ```
 */
export function getAvailableSpotsWithBreakdown(
  spots: Spot[],
  aircraft: ParkingRequest,
  dwellMask: BigUint64Array,
  currentPlacement: Placement,
  placedAircraftPolygons?: Map<string, Point[]>,
  // shapes parameter REMOVED - use spot.shape instead
  zones: Zone[] = []
): { availableSpots: Spot[]; breakdown: FilterBreakdown } {
  const breakdown: FilterBreakdown = {
    total: spots.length,
    disabled: 0,
    stagingIncompatible: 0,
    sizeIncompatible: 0,
    timeConflict: 0,
    durationExceeded: 0,
    geometryCollision: 0,
    available: 0,
  };

  let remainingSpots = spots;

  // Filter 0: Enabled status
  const enabledSpots = remainingSpots.filter((spot) =>
    isSpotEnabled(spot, zones)
  );
  breakdown.disabled = remainingSpots.length - enabledSpots.length;
  remainingSpots = enabledSpots;

  // Filter 1: Staging zone compatibility
  // Staging segments MUST use staging zones, permanent segments MUST NOT use staging zones
  const stagingCompatibleSpots = remainingSpots.filter((spot) => {
    const isStaging = isSpotInStagingZone(spot);
    if (aircraft.isStagingSegment) {
      return isStaging; // Staging segment requires staging zone
    } else {
      return !isStaging; // Permanent segment cannot use staging zone
    }
  });
  breakdown.stagingIncompatible = remainingSpots.length - stagingCompatibleSpots.length;
  remainingSpots = stagingCompatibleSpots;

  // Filter 2: Size compatibility
  const sizeCompatibleSpots = remainingSpots.filter((spot) =>
    isSizeCompatible(spot, aircraft)
  );
  breakdown.sizeIncompatible = remainingSpots.length - sizeCompatibleSpots.length;
  remainingSpots = sizeCompatibleSpots;

  // Filter 3: Time availability
  const timeAvailableSpots = remainingSpots.filter((spot) =>
    isTimeAvailable(spot, dwellMask)
  );
  breakdown.timeConflict = remainingSpots.length - timeAvailableSpots.length;
  remainingSpots = timeAvailableSpots;

  // Filter 4: Duration limit
  const durationValidSpots = remainingSpots.filter((spot) =>
    isDurationValid(spot, aircraft)
  );
  breakdown.durationExceeded = remainingSpots.length - durationValidSpots.length;
  remainingSpots = durationValidSpots;

  // Filter 5: Geometry collision (gap spots only - Phase 3)
  const availableSpots = remainingSpots.filter((spot) => {
    return !hasGeometryCollision(
      spot,
      aircraft,
      currentPlacement,
      placedAircraftPolygons
      // shapes parameter removed - using spot.shape
    );
  });
  breakdown.geometryCollision = remainingSpots.length - availableSpots.length;
  breakdown.available = availableSpots.length;

  return { availableSpots, breakdown };
}
